import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Category, Property, Tree } from '../../@types/vehicle-properties';
import PropertyTreeCreator from '../../utils/vehicle-properties/property-tree-creator';
import sortTree from '../../utils/vehicle-properties/property-tree-sorter';
import enrichWithHint from '../../utils/vehicle-properties/property-hint-enricher';
import addCustomProperties from '../../utils/vehicle-properties/custom-property-adder';
import removeProperties from '../../utils/vehicle-properties/property-remover';
import MetadataItem from './MetadataItem';

import { H5 } from '../../StyledComponents/Heading';
import { Repeat } from '../../StyledComponents/Repeat';
import { TableList } from '../../StyledComponents/TableList';
import Tabs from '../../StyledComponents/Tabs';
import { VehicleData } from '../../@types/codegen/types';

interface MetadataSubCategoryProps {
  name: string;
  properties: Property[];
}

const MetadataSubCategory = ({ name, properties }: MetadataSubCategoryProps) => (
  <Repeat>
    <H5 as="h3">{name}</H5>
    <TableList>
      {!!properties.length &&
        properties.map((property, index) => (
          <MetadataItem
            name={property.name}
            hint={property.hint}
            value={property.value}
            unit={property.unit}
            key={`${property.name}-${index}`}
          />
        ))}
    </TableList>
  </Repeat>
);

interface MetadataCaregoryProps {
  properties: Property[];
  subCategories: Category[];
}

const MetadataCategory = ({ properties, subCategories }: MetadataCaregoryProps) => (
  <>
    {!!properties.length && (
      <Repeat>
        <TableList>
          {properties.map((property, index) => (
            <MetadataItem
              name={property.name}
              hint={property.hint}
              value={property.value}
              unit={property.unit}
              key={`${property.name}-${index}`}
            />
          ))}
        </TableList>
      </Repeat>
    )}
    {!!subCategories.length &&
      subCategories.map((subCategory, index) => (
        <MetadataSubCategory
          name={subCategory.name}
          properties={subCategory.properties}
          key={`${subCategory.name}-${index}`}
        />
      ))}
  </>
);

interface PropertySetProps {
  propertySet: any;
  vehicleData: VehicleData;
  /** Already categorised attributes (public API); skips the legacy pipeline. */
  tree?: Tree;
}

const PropertySet = ({ propertySet, vehicleData, tree }: PropertySetProps) => {
  const [activeCategoryId, setActiveCategoryId] = useState(-1);
  const mappedProperties = useMemo(() => {
    if (tree) {
      return tree;
    }
    if (!propertySet) {
      return undefined;
    }

    let newProperties = removeProperties(propertySet);
    const enrichedProperties = addCustomProperties(vehicleData, newProperties);

    const created = new PropertyTreeCreator(enrichedProperties).create();
    sortTree(created);
    return created;
  }, [propertySet, tree]);

  useEffect(() => {
    const categories =
      mappedProperties?.categories?.length !== undefined ? mappedProperties?.categories : undefined;

    if (activeCategoryId < 0 && categories !== undefined && categories.length > 0) {
      setActiveCategoryId(categories[0].id);
    }
  }, [mappedProperties]);

  const onTab = useCallback((id: number) => setActiveCategoryId(id), []);

  const activeCategory = mappedProperties?.categories?.find(
    (category) => category.id === activeCategoryId
  );

  return (
    <>
      <Repeat>
        <Tabs
          tabs={
            mappedProperties?.categories?.map((category) => ({
              name: `${category.id}`,
              displayName: category.name,
              onClick: () => onTab(category.id),
            })) || []
          }
          active={`${activeCategoryId}`}
        />
      </Repeat>
      {activeCategory && (
        <Repeat>
          <MetadataCategory
            properties={activeCategory.properties}
            subCategories={activeCategory.subCategories}
          />
        </Repeat>
      )}
    </>
  );
};

export default PropertySet;
